/**
 * Core Web Vitals field monitoring (CWV-14).
 *
 * The audit could only see lab data: of 41 crawled URLs just two came back with a
 * CrUX assessment and three with a field LCP, because no single URL gets enough
 * real-user traffic for Google to report on it. Lab numbers are a diagnostic;
 * field numbers are what ranking uses. This collects the field numbers ourselves.
 *
 * Deliberately hand-written rather than pulling in Google's web-vitals library.
 * Four of the CWV tickets on this branch were about removing third-party
 * JavaScript, so adding a CDN dependency to measure the effect of removing CDN
 * dependencies would be an odd way to finish. This is ~2 KB, has no origin of its
 * own, and every metric below is computed straight from the browser's own
 * PerformanceObserver entries. If the edge cases ever matter more than the weight
 * -- web-vitals handles bfcache restores, soft navigations and prerendering more
 * thoroughly than this does -- swapping it in means replacing the collectors and
 * keeping `report()`.
 *
 * Where the numbers go: `window.dataLayer`, which GTM already owns on this store,
 * plus Clarity custom tags so session recordings can be filtered by how the page
 * actually performed. Nothing is transmitted by this file itself -- no fetch, no
 * beacon, no endpoint. Until someone builds a GTM tag on the `web_vitals` event
 * the data stays in the browser, which also means consent handling stays where it
 * already is, in GTM and Pandectes, rather than being reinvented here.
 *
 * Disable with the "Collect Core Web Vitals field data" setting.
 */
(function () {
  'use strict';

  if (!('PerformanceObserver' in window)) return;

  /* Google's published thresholds. Anything past the second number is "poor". */
  var THRESHOLDS = {
    LCP: [2500, 4000],
    INP: [200, 500],
    CLS: [0.1, 0.25],
    FCP: [1800, 3000],
    TTFB: [800, 1800],
  };

  var metrics = {};
  var reported = false;

  /* A page that is never visible -- a prerender, a background tab, a bot -- never
     records a paint, so FCP and LCP simply do not exist for it. Verified against
     the live storefront in a hidden tab: TTFB arrived, FCP and LCP never did.
     Reporting those loads anyway would seed the dataset with rows that have a
     TTFB and nothing else, which drags any average toward a number that describes
     no real visit. So the page has to have been seen at least once to count. */
  var wasVisible = document.visibilityState === 'visible';
  if (!wasVisible) {
    addEventListener('visibilitychange', function onFirstShow() {
      if (document.visibilityState === 'visible') {
        wasVisible = true;
        removeEventListener('visibilitychange', onFirstShow);
      }
    });
  }

  function rate(name, value) {
    var t = THRESHOLDS[name];
    if (!t) return 'unknown';
    return value <= t[0] ? 'good' : value <= t[1] ? 'needs-improvement' : 'poor';
  }

  function observe(type, cb, opts) {
    try {
      var po = new PerformanceObserver(function (list) {
        list.getEntries().forEach(cb);
      });
      po.observe(Object.assign({ type: type, buffered: true }, opts || {}));
      return po;
    } catch (e) {
      /* An engine that does not know this entry type just contributes no metric. */
      return null;
    }
  }

  /* ---------------------------------------------------------------- LCP.
     Keep taking the latest candidate until the first interaction, which is where
     the spec stops counting. */
  var lcpObserver = observe('largest-contentful-paint', function (entry) {
    metrics.LCP = entry.startTime;
  });

  function stopLCP() {
    if (lcpObserver) {
      lcpObserver.takeRecords().forEach(function (e) { metrics.LCP = e.startTime; });
      lcpObserver.disconnect();
      lcpObserver = null;
    }
  }
  ['keydown', 'pointerdown'].forEach(function (t) {
    addEventListener(t, stopLCP, { once: true, capture: true });
  });

  /* ---------------------------------------------------------------- CLS.
     CLS is not a plain sum: it is the largest burst of shifts, where a burst ends
     after a 1s gap or 5s of total elapsed time. Summing everything instead would
     overstate long-lived pages badly. */
  var clsValue = 0;
  var sessionValue = 0;
  var sessionStart = 0;
  var sessionLast = 0;

  observe('layout-shift', function (entry) {
    if (entry.hadRecentInput) return;
    if (sessionValue && entry.startTime - sessionLast < 1000 && entry.startTime - sessionStart < 5000) {
      sessionValue += entry.value;
    } else {
      sessionValue = entry.value;
      sessionStart = entry.startTime;
    }
    sessionLast = entry.startTime;
    if (sessionValue > clsValue) clsValue = sessionValue;
    metrics.CLS = clsValue;
  });

  /* ---------------------------------------------------------------- INP.
     The reported value is the 98th percentile of interaction latencies, which for
     a page with fewer than 50 interactions is simply the slowest one. Entries are
     grouped by interactionId so that a click's pointerdown/pointerup/click all
     count once, as the longest of the group. */
  var interactions = {};

  observe('event', function (entry) {
    if (!entry.interactionId) return;
    var cur = interactions[entry.interactionId];
    if (!cur || entry.duration > cur) interactions[entry.interactionId] = entry.duration;
  }, { durationThreshold: 40 });

  function computeINP() {
    var values = Object.keys(interactions).map(function (k) { return interactions[k]; });
    if (!values.length) return;
    values.sort(function (a, b) { return b - a; });
    metrics.INP = values[Math.min(values.length - 1, Math.floor(values.length / 50))];
  }

  /* ------------------------------------------------------------ FCP, TTFB. */
  observe('paint', function (entry) {
    if (entry.name === 'first-contentful-paint') metrics.FCP = entry.startTime;
  });

  observe('navigation', function (entry) {
    metrics.TTFB = entry.responseStart;
  });

  /* ---------------------------------------------------------------- Report.
     Once per page load, on the last moment the browser reliably gives us --
     visibilitychange to hidden fires where unload does not, on mobile especially. */
  function report() {
    if (reported || !wasVisible) return;
    reported = true;
    stopLCP();
    computeINP();

    var tpl = (window._template && window._template.name) || 'unknown';
    var conn = navigator.connection || {};

    var payload = {
      event: 'web_vitals',
      cwv_template: tpl,
      cwv_path: location.pathname,
      cwv_connection: conn.effectiveType || 'unknown',
      cwv_save_data: !!conn.saveData,
    };

    Object.keys(metrics).forEach(function (name) {
      var value = metrics[name];
      /* CLS is unitless and small; the rest are milliseconds. Rounding keeps the
         analytics payload readable without losing anything that matters. */
      var rounded = name === 'CLS' ? Math.round(value * 10000) / 10000 : Math.round(value);
      payload['cwv_' + name.toLowerCase()] = rounded;
      payload['cwv_' + name.toLowerCase() + '_rating'] = rate(name, value);
    });

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    } catch (e) {}

    /* Clarity custom tags make a session recording filterable by how the page
       performed, which is the fastest way to watch a poor LCP actually happen. */
    try {
      if (typeof window.clarity === 'function') {
        Object.keys(metrics).forEach(function (name) {
          window.clarity('set', 'cwv_' + name.toLowerCase(), rate(name, metrics[name]));
        });
        window.clarity('set', 'cwv_template', tpl);
      }
    } catch (e) {}

    /* Left on the page on purpose: `window.__cwv` is how you check this is working
       without waiting for an analytics pipeline to be built. */
    window.__cwv = payload;
  }

  addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') report();
  });
  /* Safari has historically not fired the above on tab close. */
  addEventListener('pagehide', report, { capture: true });
})();
